/* eslint-disable  */
//@ts-nocheck
import { format } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { StreamChat } from 'stream-chat';
import {
  Channel,
  Chat,
  MessageInput,
  useChannelActionContext,
  useChannelStateContext,
  VirtualizedMessageList,
  useCreateChatClient,
} from 'stream-chat-react';
import 'stream-chat-react/dist/css/v2/index.css';
import './chat.css';
import * as styles from './LiveStreamChat.css';
import auth from '../auth';
import { HStack, VStack } from '../common/stack';
import { Tag } from '../common/tag';
import { ChatAvatar } from './Avatar';
import ArrowRight from '../../images/arrow-right.svg?react';

const boxPadding = 10;
const currentUserPadding = 5;
const totalPadding = boxPadding + currentUserPadding;

const SendMessage = ({ ...props }) => {
  const [text, setText] = useState('');
  const { sendMessage } = useChannelActionContext();
  const send = useCallback(() => {
    if (text.length) {
      sendMessage({ text });
      setText('');
    }
  }, [text]);
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      send();
    }
  };
  return (
    <div className={styles.messageInputContainer}>
      <input
        className={styles.input}
        {...props}
        value={text}
        placeholder="Write your message..."
        onKeyPress={handleKeyPress}
        onChange={(e) => setText(e.target.value)}
      />
      <ArrowRight
        className={styles.sendIcon}
        alt="Send message"
        onClick={send}
      />
    </div>
  );
};

const getWatcherText = (watcherCount) => {
  const noun = watcherCount === 1 ? 'user' : 'users';
  return `${watcherCount} ${noun}`;
};

const LiveChat = ({ userId }) => {
  const { channel } = useChannelStateContext();
  // TODO: use GetStream SDK instead of long polling. SDK doesn't work.
  const watchersQuery = useQuery({
    queryKey: ['watchersCount'],
    queryFn: () =>
      channel.query({
        data: {},
      }),
    enabled: !!channel,
    refetchInterval: 5000,
  });

  return (
    <div className={styles.chatLayout}>
      <div className={styles.liveChatHeader}>
        <VStack spacing={3}>
          <HStack spacing={6}>
            <span className={styles.title}>Live chat</span>
            <Tag color="#F13174">
              {getWatcherText(
                !watchersQuery.data ? 1 : watchersQuery.data.watcher_count,
              )}
            </Tag>
          </HStack>
          <span className={styles.description}>
            We show messages from the entire livestream history
          </span>
        </VStack>
      </div>
      <div className={styles.chatContainer}>
        <VirtualizedMessageList
          stickToBottomScrollBehavior="auto"
          defaultItemHeight={54}
          customMessageRenderer={(messages, index) => (
            <LiveStreamMessage userId={userId} {...messages[index]} />
          )}
        />
      </div>
      <MessageInput />
    </div>
  );
};

const LiveStreamMessage = (props) => {
  const {
    userId: currentUserId,
    user: { id, image, name },
    created_at,
    text,
  } = props;

  const isMessageFromCurrentUser = id === currentUserId;
  return (
    <div className={styles.messageWrapper}>
      <div
        className="flex rounded-lg w-full"
        style={{
          padding: `${currentUserPadding}px`,
          border: `${
            isMessageFromCurrentUser
              ? '1px solid rgba(99, 103, 239, 0.45)'
              : 'none'
          }`,
          background: `${isMessageFromCurrentUser ? '#F2F4FD' : 'none'}`,
        }}
      >
        <div className="flex-none">
          <ChatAvatar src={image} username={name} />
        </div>
        <div className="flex flex-col ml-3 flex-1 w-full">
          <div className="flex items-center">
            <span className={styles.username}>{name}</span>
            <span className="text-gray-500 text-xs ml-2">
              {format(new Date(created_at), 'MMM d, h:mmaa')}
            </span>
          </div>
          <div
            style={{ wordBreak: 'break-word' }}
            className="text-sm break-words max-w-full"
          >
            {text}
          </div>
        </div>
      </div>
    </div>
  );
};

const customClasses = {
  virtualMessage: 'custom-virtualMessage',
};

export const LiveStreamChat = ({ influencerId, influencer, streamToken }) => {
  const { user_id: userId } = jwtDecode(streamToken);

  const chatClient = useCreateChatClient({
    apiKey: import.meta.env.VITE_STREAM_CHAT_ID,
    userData: { id: userId },
    tokenOrProvider: streamToken,
  });

  const influencerAvatar = `${influencer?.mediaAvatar?.cdn01}${influencer?.mediaAvatar?.image}`;
  const influencerName = `${influencer.firstName} ${influencer.lastName}`;

  const [channel, setChannel] = useState(null);
  useEffect(() => {
    const initChat = async () => {
      try {
        if (!streamToken) return;

        await chatClient.connectUser(
          {
            id: userId,
            name: influencerName,
            image: influencerAvatar,
          },
          streamToken,
        );

        const ch = await chatClient.channel(
          'livestream',
          `live-stream-chat-${influencerId}`,
          {},
        );

        await ch.watch();

        setChannel(ch);
      } catch (err) {
        console.error(err);
        setChannel(null);
      }
    };

    chatClient && initChat();
    return () => {
      chatClient && chatClient.disconnectUser();
    };
  }, [influencerId, streamToken, chatClient]);

  if (!channel) {
    return <div>Loading chat...</div>;
  }

  return (
    <div id="livestream">
      <Chat client={chatClient} customClasses={customClasses} theme="light">
        <Channel channel={channel} Input={SendMessage}>
          <LiveChat userAvatar={influencerAvatar} userId={userId} />
        </Channel>
      </Chat>
    </div>
  );
};
